/**
 * One send's Retry state — moved verbatim out of `../DispatchInspector.tsx`
 * (plan 2026-09-27-003, U6).
 */
import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { addErrorToast, addToast } from '@clay-run/glaze/toast/toast-queue';

import { useApi } from '../../../../_shared/hooks';
import { retryDispatchRequest } from '../../../api-client';
import { playSendsQueryKey, retryResultToast } from '../../../dispatch-inspector-logic';
import type { PlaybooksDispatchLogRow } from '../../../schemas';
import { rowActionErrorMessage } from '../../../settings-page-logic';

// Retry (plan 2026-09-26-003, U7) — one send's failed START rows.
//
// The server decides what actually happens (`dispatch-retry.ts` checks with
// Clay before resending anything); this owns only the in-flight state of the
// buttons. Plain `useState` for what renders (UI-only, never seeded from query
// data — the same category as `openSendIds` above), plus a ref as the
// synchronous double-click guard: two clicks landing in one event batch both
// see the pre-render state, so state alone cannot stop the second request.
// The ref holds no lifetime state a remount would need reset (StrictMode) — an
// entry lives only between a request's start and its `finally`.

export interface SendRetry {
  /** Rows whose own Retry is in flight. */
  pendingLogIds: ReadonlySet<string>;
  /** This send's "Retry all stopped or failed" is in flight — every row button waits. */
  bulkPending: boolean;
  /**
   * The server already acted on this row (requeued it, found Clay's run, or
   * another retry won) and the log has not caught up yet — the row still
   * reads the way it did when Retry was clicked. Holding the button here is
   * what makes a double-click one request even when the first answer comes
   * back between the two clicks.
   */
  isSettling: (row: PlaybooksDispatchLogRow) => boolean;
  retryRow: (logId: string) => Promise<void>;
  retryAll: () => Promise<void>;
}

const BULK_RETRY_KEY = '__all__';

/** A row's retry-relevant state — changes once the server's retry lands in the log. */
function retryFingerprint(row: PlaybooksDispatchLogRow): string {
  return `${row.result ?? ''}:${row.retry_count ?? 0}:${row.result_status ?? ''}`;
}

/**
 * Outcomes after which the row WILL change — anything else leaves it
 * retryable. True for both arms: a refusal the server requeued moves from
 * `sent` + `clay_failed` to `queued` with no `result_status`, which changes
 * {@link retryFingerprint}. `copy_rebuild_failed` (sharing rules (m)) is
 * deliberately NOT here: the row is left exactly as it was, so the button must
 * stay live once the rep has fixed whatever the toast named.
 */
const SETTLING_OUTCOMES: ReadonlySet<string> = new Set(['requeued', 'clay_has_run', 'already_retried']);

export function useSendRetry(
  playId: string,
  dispatchId: string | null,
  rows: readonly PlaybooksDispatchLogRow[],
): SendRetry {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();
  const [pendingLogIds, setPendingLogIds] = useState<ReadonlySet<string>>(new Set());
  const [bulkPending, setBulkPending] = useState(false);
  const [settling, setSettling] = useState<ReadonlyMap<string, string>>(new Map());
  const inFlight = useRef<Set<string>>(new Set());

  async function run(key: string, logIds: string[] | undefined): Promise<void> {
    if (dispatchId === null || inFlight.current.has(key)) return;
    inFlight.current.add(key);
    // What each row looked like when the rep clicked.
    const clicked = new Map(rows.map((row) => [row.id, retryFingerprint(row)]));
    if (logIds === undefined) setBulkPending(true);
    else setPendingLogIds((prev) => new Set(prev).add(key));
    try {
      const response = await retryDispatchRequest(apiFetch, playId, {
        dispatchId,
        ...(logIds !== undefined ? { logIds } : {}),
      });
      setSettling((prev) => {
        const next = new Map(prev);
        for (const result of response.results) {
          const fingerprint = clicked.get(result.logId);
          if (fingerprint !== undefined && SETTLING_OUTCOMES.has(result.outcome)) next.set(result.logId, fingerprint);
        }
        return next;
      });
      await queryClient.invalidateQueries({ queryKey: playSendsQueryKey(playId) });
      addToast(retryResultToast(response));
    } catch (err) {
      // The server's own `problem()` message when there is one — never
      // `.message`, which can be a raw status code (settings page, Codex F3).
      addErrorToast({ content: rowActionErrorMessage(err, "Couldn't retry. Try again in a minute.") });
    } finally {
      inFlight.current.delete(key);
      if (logIds === undefined) setBulkPending(false);
      else
        setPendingLogIds((prev) => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
    }
  }

  return {
    pendingLogIds,
    bulkPending,
    isSettling: (row) => settling.get(row.id) === retryFingerprint(row),
    retryRow: (logId) => run(logId, [logId]),
    retryAll: () => run(BULK_RETRY_KEY, undefined),
  };
}
